/**
 * PATH: packages/email-hub/src/feature/lib/headerActions.ts
 * FILE_ID: EMAILHUB-HEADERACTIONS-001
 * PURPOSE: Pure resolver that turns an email's STATE into the
 *          contextual VERBS the reader header should surface — the
 *          "what do I do next?" half of the header (the chip strip
 *          carries the "what is this?" half).
 *
 * WHY:     Operator (2026-06-02): "First principle design the usage of
 *          this whitespace... easy actions like invite team member when
 *          relevant (quote request / kanban board), click and allocate
 *          a lane, change the status if it's a job action."
 *
 *          First principle: the header's job is orientation + the next
 *          action, NOT chip density. Every email resolves to ONE
 *          dominant disposition; surfacing that as a real button (with
 *          1–3 supporting verbs) beats a fixed toolbar of everything or
 *          a read-only label. The long tail stays in the right-click
 *          menu we already ship.
 *
 *          This is a PURE I → O function so the mapping is unit-tested
 *          without mounting React. `SmartActionBar` renders the model;
 *          the owner / invite / when affordances live in the reused
 *          `EmailAllocationRail` and are NOT modelled here.
 *
 * RELATED:
 *   - ./headerActions.test.ts
 *   - ../components/SmartActionBar.tsx (renders this model)
 *   - ../components/EmailAllocationRail.tsx (owner + invite + when)
 */

import type { Email } from '../hooks/useEmails'

export type HeaderActionKind =
  /** quote_request → hand the tender to an estimator (renders the
   *  self-contained EstimatorAllocator, not a plain button). */
  | 'allocate_estimator'
  /** financial-in (bill / supplier / invoice) → route to Bills. */
  | 'send_to_bills'
  /** already tracked on a Kanban board → open its card. (Status =
   *  lane, editable from the chip strip's AllocationChip.) */
  | 'open_card'
  /** linked to a job → the job is the source of truth; navigate. */
  | 'open_job'
  /** junk / spam → get it out of the inbox. */
  | 'move_to_trash'
  /** junk / spam → arm a sender-domain auto-trash rule (also
   *  retroactively bins matches). */
  | 'auto_junk_rule'
  /** "I'm done with this" → archive it out of the inbox. The
   *  universal exit verb: available on every disposition except
   *  junk (where trash is already terminal). For plain mail with no
   *  other obvious next step (a system notice, a newsletter) this is
   *  the dominant primary — the operator's one-click "clear it". */
  | 'mark_done'
  /** "this is junk" → bin it now, with a caret for "junk options"
   *  (auto-trash this sender / whole domain) that retroactively bins
   *  existing matches AND arms a prospective rule. Surfaced on every
   *  non-junk disposition so promo / newsletter mail that the AI
   *  auto-filed onto a board (e.g. a marketing blast parked on
   *  "My Inbox · Triage") can be binned in one click WITHOUT having
   *  to re-categorise it first. On already-junk/spam mail the
   *  `move_to_trash` + `auto_junk_rule` pair already covers this, so
   *  `mark_junk` is not added there (it would be redundant). */
  | 'mark_junk'

/** Verbs that render as secondary (outline-chip) buttons.
 *  `allocate_estimator` is a self-contained PRIMARY affordance (it
 *  renders the EstimatorAllocator, not a plain button) and is never a
 *  secondary chip — excluding it here lets SmartActionBar map
 *  `secondary` straight onto `ActionButton`, whose `kind` likewise
 *  excludes `allocate_estimator`. */
export type SecondaryActionKind = Exclude<HeaderActionKind, 'allocate_estimator'>

export interface HeaderActionsModel {
  /** The dominant next step — rendered as a filled primary control.
   *  `null` for plain mail with no obvious disposition (a newsletter,
   *  a personal note): the strip's "Add to board" + the owner/invite
   *  rail carry triage, and the bar collapses. */
  primary: HeaderActionKind | null
  /** Supporting verbs, in render order — outline chips. */
  secondary: SecondaryActionKind[]
}

/** Categories whose obvious disposition is "send to Bills". */
const FINANCIAL_IN = new Set(['bill', 'supplier', 'invoice'])
/** Buckets we want straight out of the inbox. `junk` is the
 *  rule/user-only bucket; `spam` is the classifier's promo tag. */
const JUNK = new Set(['junk', 'spam'])

/**
 * Resolve an email into its header action model.
 *
 * Priority order encodes the first-principles "one dominant
 * disposition" rule — the FIRST matching state wins the primary:
 *
 *   1. junk/spam       → trash (+ auto-trash rule)        [terminal]
 *   2. quote_request   → allocate estimator
 *   3. financial-in    → send to Bills
 *   4. on a board      → open card (lane = status, edited in the strip)
 *   5. linked to a job → open job (source of truth)
 *   6. plain mail      → DONE (archive it out of the inbox)
 *
 * "Go to source" links (open card / open job) that aren't the primary
 * fall through to `secondary` so the operator can always reach the
 * card and the job from the header. `mark_done` (the universal
 * "I've handled this, clear it" exit) and `mark_junk` (the universal
 * "this is junk, bin it" exit) both tail `secondary` on every non-junk
 * state. `mark_done` is the PRIMARY for plain mail (a system notice, a
 * newsletter) that has no other obvious next step; `mark_junk` rides
 * alongside it so a promo blast the AI auto-filed onto a board can be
 * binned without re-categorising first.
 */
export function deriveHeaderActions(
  email: Pick<Email, 'category' | 'allocation_summary' | 'job_id' | 'suggested_job'>,
): HeaderActionsModel {
  const category = email.category ?? ''
  const onBoard = Boolean(email.allocation_summary)
  const hasJob = email.job_id != null

  // Cross-cutting "go to source" verbs, appended to secondary when
  // they aren't the primary for this state. `mark_done` then `mark_junk`
  // always tail the secondary list so there is a one-click clear AND a
  // one-click bin on every non-junk state.
  const tail = (exclude: HeaderActionKind): SecondaryActionKind[] => {
    const out: SecondaryActionKind[] = []
    if (onBoard && exclude !== 'open_card') out.push('open_card')
    if (hasJob && exclude !== 'open_job') out.push('open_job')
    if (exclude !== 'mark_done') out.push('mark_done')
    out.push('mark_junk')
    return out
  }

  if (JUNK.has(category)) {
    // Trash IS the "done" for junk — adding a second exit verb is noise.
    return { primary: 'move_to_trash', secondary: ['auto_junk_rule'] }
  }
  if (category === 'quote_request') {
    return { primary: 'allocate_estimator', secondary: tail('allocate_estimator') }
  }
  if (FINANCIAL_IN.has(category)) {
    return { primary: 'send_to_bills', secondary: tail('send_to_bills') }
  }
  if (onBoard) {
    return { primary: 'open_card', secondary: tail('open_card') }
  }
  if (hasJob) {
    return { primary: 'open_job', secondary: tail('open_job') }
  }
  // Plain mail (system notices, newsletters): the dominant next step is
  // to clear it (Done primary); Junk rides alongside for promo blasts.
  return { primary: 'mark_done', secondary: tail('mark_done') }
}

/** Whether the bar has anything to render for this email. Lets the
 *  host skip the wrapper entirely (and its border/spacing) for plain
 *  mail when the owner/invite rail is also absent. */
export function hasHeaderActions(model: HeaderActionsModel): boolean {
  return model.primary !== null || model.secondary.length > 0
}
